import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { colonyApi } from "@/lib/colonyApi";
import { TutorialButton } from "@/components/tutorials/TutorialButton";
import { formatErr, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Camera, Plus, X, ImageIcon, Reply, CheckCircle2, PauseCircle, CalendarClock, Pencil, ShoppingCart } from "lucide-react";
import { AddToShoppingDialog, ReplyShoppingSection, submitShoppingItems, TicketShoppingStrip } from "@/components/ShoppingList";
import { TicketsAiButton } from "@/components/TicketsAiButton";
import { CollapseToggle } from "@/components/TicketCollapse";
import { useColonyBrand } from "./useColonyBrand";
import { useColonyAuth } from "@/contexts/ColonyAuthContext";
import { TicketEditDialog } from "@/components/TicketEditDialog";
import { TicketSearchControl, ticketMatches, ticketInDates, Highlight } from "@/components/TicketSearch";
// Photos are compressed in-browser before upload — no size rejections.
import { processMediaFiles } from "@/lib/livePhotoMedia";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { queueIfOffline } from "@/lib/offlineQueue";
import PhotoAddButtons, { pasteToFiles } from "@/components/PhotoAddButtons";
import { useAutoTranslate } from "@/lib/useAutoTranslate";
import { LazyPhotoStrip } from "@/components/PhotoLightbox";
import { useSelfHeight } from "@/lib/useSelfHeight";
import { useStickyBar } from "@/lib/useStickyBar";
import { useCalendarPage } from "@/lib/useCalendarPage";
import { CalendarYearSelect } from "@/components/CalendarYearSelect";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { MobileStatusStrip } from "@/components/MobileStatusStrip";
import { HoverTime } from "@/components/HoverTime";

const STATUS_META = {
  scheduled: { label: "Scheduled", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  open: { label: "Open", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  hold: { label: "On hold", cls: "bg-purple-100 text-purple-800 border-purple-300" },
  done: { label: "Closed", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "Cancelled", cls: "bg-rose-50 text-slate-700 border-rose-300" },
};

const FILTER_TABS = ["all", "scheduled", "open", "in_progress", "hold", "done"];
const FILTER_LABELS = { all: "All", scheduled: "Scheduled", open: "Open", in_progress: "In progress", hold: "On hold", done: "Closed" };

export default function ColonyTickets() {
  useColonyBrand();
  const [filterBarRef, filterBarHeight] = useSelfHeight();
  const isMobileViewport = useMediaQuery("(max-width: 767px)");
  const stickyOffsetVar = isMobileViewport ? "--colony-mobile-top" : "--sticky-header-h";
  const [sentinelRef, isStuck] = useStickyBar(stickyOffsetVar);
  const [colonies, setColonies] = useState([]);
  const ticketPage = useCalendarPage({ client: colonyApi, path: "/colony/tickets", initialTicket: new URLSearchParams(window.location.search).get("ticket") || "" });
  const { items: tickets, setItems: setTickets, reload: reloadTickets } = ticketPage;
  const [profileLoading, setLoading] = useState(true);
  const loading = profileLoading || ticketPage.loading;
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const counts = useMemo(() => {
    const c = {};
    for (const t of tickets) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [tickets]);
  const visible = useMemo(() => {
    const q = search.trim();
    const opts = {
      colonyName: (id) => colonies.find((c) => c.id === id)?.name || "",
      statusLabel: (s) => STATUS_META[s]?.label || "",
    };
    return tickets
      .filter((t) => q ? true : (statusFilter === "all" || t.status === statusFilter))
      .filter((t) => ticketMatches(t, q, opts))
      .filter((t) => ticketInDates(t, dateFrom, dateTo));
  }, [tickets, statusFilter, search, dateFrom, dateTo, colonies]);

  // Everyone reads their own language: Spanish replies show in English here.
  const dynTexts = useMemo(
    () => visible.flatMap((tk) => [tk.title, tk.description, ...(tk.notes || []).map((n) => n.body)]),
    [visible]
  );
  const atr = useAutoTranslate(colonyApi, dynTexts);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const mine = await colonyApi.get("/colony/me");
      setColonies(mine.data?.colonies || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const load = useCallback(() => Promise.all([loadProfile(), reloadTickets()]), [loadProfile, reloadTickets]);
  useEffect(() => { loadProfile(); }, [loadProfile]);
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current || !tickets.length) return;
    const tid = new URLSearchParams(window.location.search).get("ticket");
    if (!tid) return;
    deepLinkedRef.current = true;
    setStatusFilter("all");
    setSearch("");
    window.history.replaceState({}, "", window.location.pathname);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="colony-ticket-${tid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-primary", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 2800);
      }
    }, 300);
  }, [tickets]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("offline-synced", h);
    return () => window.removeEventListener("offline-synced", h);
  }, [load]);

  return (
    <div className="space-y-5" data-testid="colony-tickets-page">
      {ticketPage.error && <div role="alert" className="text-sm text-destructive" data-testid="colony-tickets-year-error">{ticketPage.error} <Button variant="outline" size="sm" onClick={ticketPage.reload} data-testid="colony-tickets-year-retry">Retry</Button></div>}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="overline">Service requests</p>
          <div className="flex flex-wrap items-center gap-3"><h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Report what needs doing</h1><TutorialButton audience="colony" section="tickets" /></div>
          <p className="hidden sm:block text-sm text-muted-foreground mt-1">
            Something needs fixed, cleaned, or checked? Send a photo and we&apos;ll pick it up.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2" data-testid="colony-ticket-top-actions">
        <CalendarYearSelect page={ticketPage} id="colony-tickets-year" onChange={() => { setDateFrom(""); setDateTo(""); }} />
        <Button
          disabled={!colonies.length}
          onClick={() => setOpen(true)}
          className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white h-10"
          data-testid="colony-new-ticket"
        >
          <Plus className="w-4 h-4 mr-2" /> New request
        </Button>
        </div>
      </div>

      {/* Status tabs + compact search. Renders in normal flow until
          scrolled up to the viewport edge on phones (below the header on
          desktop), then switches to real `position:
          fixed` (a sentinel that mimics `sticky` via IntersectionObserver
          — plain CSS `sticky` here would hit a long-standing iOS Safari
          WebKit bug with two stacked/nested sticky elements, this bar +
          the header above, where the second one stops tracking scroll).
          Only fixed while actually stuck, so it never overlaps the title
          / "New request" button that sits above it before that. */}
      <div ref={sentinelRef} />
      <div
        ref={filterBarRef}
        data-testid="colony-status-bar"
        data-stuck={isStuck ? "true" : "false"}
        className={isStuck
          ? "fixed inset-x-0 z-20 max-w-6xl mx-auto !mt-0 px-4 py-2 bg-emerald-50/95 backdrop-blur-sm"
          : "relative z-20 -mx-4 px-4 py-2 bg-emerald-50/95 backdrop-blur-sm"}
        style={isStuck ? { top: `var(${stickyOffsetVar}, 0px)` } : undefined}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-1.5" data-testid="colony-status-tabs">
          <div className="min-w-0 flex-1">
          <MobileStatusStrip id="colony-status-strip">
          {FILTER_TABS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              aria-pressed={statusFilter === s}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${statusFilter === s ? (s === "all" ? "bg-emerald-700 text-white border-emerald-700" : `${STATUS_META[s]?.cls || ""} shadow-sm`) : "bg-background text-muted-foreground border-border hover:border-emerald-600"}`}
              data-testid={`colony-status-tab-${s}`}
            >
              {FILTER_LABELS[s]} ({s === "all" ? tickets.length : counts[s] || 0})
            </button>
          ))}
          </MobileStatusStrip>
          </div>
          <span className="portal-filter-search ml-auto shrink-0">
            <TicketSearchControl
              search={search} setSearch={setSearch}
              dateFrom={dateFrom} setDateFrom={setDateFrom}
              dateTo={dateTo} setDateTo={setDateTo}
              testidPrefix="colony-tickets"
              responsive={isMobileViewport}
            />
          </span>
        </div>
      </div>
      {isStuck && <div style={{ height: filterBarHeight }} aria-hidden="true" />}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : tickets.length === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="colony-tickets-empty">
          <h3 className="font-display text-lg font-bold">No requests yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Click &quot;New request&quot; above to file your first one.
          </p>
        </Card>
      ) : visible.length === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="colony-tickets-filter-empty">
          <p className="text-sm text-muted-foreground">
            No {FILTER_LABELS[statusFilter].toLowerCase()} requests right now.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map((t) => (
            <ColonyTicketCard
              key={t.id}
              ticket={t}
              highlight={search}
              atr={atr}
              colonyName={colonies.find((c) => c.id === t.colony_id)?.name || ""}
              onUpdated={(u) => setTickets((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
            />
          ))}
        </div>
      )}

      <NewTicketDialog
        open={open}
        onOpenChange={setOpen}
        colonies={colonies}
        onSaved={() => { setOpen(false); load(); }}
      />
      <TicketsAiButton key={ticketPage.year || "loading"} calendarYear={ticketPage.year} client={colonyApi} askPath="/colony/tickets/ask" hidden={ticketPage.loading || !!ticketPage.error || !ticketPage.year} />
    </div>
  );
}

function ColonyTicketCard({ ticket, colonyName, onUpdated, highlight = "", atr = (x) => x }) {
  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  const { user } = useColonyAuth();
  const isMine = !!user?.id && ticket.created_by_user_id === user.id;
  const [replyOpen, setReplyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(ticket.status === "in_progress");
  const [threadOpen, setThreadOpen] = useState(ticket.status === "in_progress");
  // Show the scheduled-date input only when the colony user opts into
  // it — keeps the default view uncluttered per the spec: date should
  // be an OPTIONAL step, not a default field.
  const [dateOpen, setDateOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState(ticket.scheduled_for || "");
  const [busy, setBusy] = useState(false);
  const closed = ticket.status === "done" || ticket.status === "cancelled";

  const patch = async (payload, msg) => {
    setBusy(true);
    try {
      const { data } = await colonyApi.patch(`/colony/tickets/${ticket.id}`, payload);
      onUpdated(data);
      if (msg) toast.success(msg);
    } catch (e) {
      if (queueIfOffline(e, { portal: "colony", method: "patch", path: `/colony/tickets/${ticket.id}`, body: payload, label: `Update ticket "${ticket.title}"` })) {
        onUpdated({ ...ticket, ...payload });
      } else {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const closeTicket = () => patch({ status: "done" }, "Ticket closed. The office has been notified.");
  const putOnHold = () => patch({ status: "hold" }, "Ticket placed on hold. The office has been notified.");
  const saveDate = () => patch({ scheduled_for: pendingDate || null }, pendingDate ? "Date saved" : "Date cleared");
  const clearDate = () => { setPendingDate(""); patch({ clear_scheduled_for: true }, "Date cleared"); };

  return (
    <div className={`border rounded-sm p-4 ${ticket.urgent && (ticket.status === "open" || ticket.status === "in_progress") ? "border-red-300 bg-red-50/70" : "border-border bg-background"}`} data-testid={`colony-ticket-${ticket.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-base"><Highlight text={atr(ticket.title)} query={highlight} /></h3>
            {ticket.urgent && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-red-400 bg-red-100 text-red-800 font-bold" data-testid={`colony-ticket-urgent-${ticket.id}`}>Urgent</span>
            )}
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${meta.cls}`}>{meta.label}</span>
            {ticket.scheduled_for && (
              <span
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-purple-300 bg-purple-50 text-purple-800 inline-flex items-center gap-1"
                title={ticket.status === "scheduled" ? "Will be filed automatically at this date & time" : "Scheduled to be revisited on this date"}
                data-testid={`colony-ticket-scheduled-${ticket.id}`}
              >
                <CalendarClock className="w-3 h-3" /> {fmtDateTime(ticket.scheduled_for)}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {colonyName ? `${colonyName} · ` : ""}Filed <HoverTime iso={ticket.created_at} label="Filed " testId={`colony-ticket-filed-${ticket.id}`} />
          </div>
          <CollapseToggle
            open={detailsOpen}
            onClick={() => setDetailsOpen((v) => !v)}
            label={detailsOpen ? "Hide details" : "Show details"}
            testid={`colony-ticket-details-toggle-${ticket.id}`}
            className="mt-2"
          />
          {detailsOpen && ticket.description && (
            <p className="text-sm mt-2 whitespace-pre-wrap"><Highlight text={atr(ticket.description)} query={highlight} /></p>
          )}
          {/* Photos + shopping items always visible — never hidden behind the Details toggle. */}
          {(ticket.photos?.length || ticket.photo_count) ? (
            <div className="mt-3">
              <LazyPhotoStrip
                photos={ticket.photos}
                count={ticket.photo_count}
                fetchPhotos={() => colonyApi.get(`/colony/tickets/${ticket.id}/photos`).then((r) => r.data.photos)}
                alt="photo"
                cacheKey={`ticket-${ticket.id}`}
              />
            </div>
          ) : null}
          <TicketShoppingStrip
            count={ticket.shopping_count}
            fetchItems={() => colonyApi.get(`/colony/tickets/${ticket.id}/shopping`).then((r) => r.data)}
            client={colonyApi}
            basePath="/colony"
            testid={`colony-ticket-shopping-items-${ticket.id}`}
          />
        </div>

        {/* Action column — right rail on desktop, full-width 2-col grid on phones */}
        {!closed && (
          <div className="w-full grid grid-cols-2 gap-2 sm:w-auto sm:flex sm:flex-col sm:min-w-[180px]">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm justify-start"
              onClick={() => setReplyOpen((v) => !v)}
              data-testid={`colony-reply-toggle-${ticket.id}`}
            >
              <Reply className="w-3.5 h-3.5 mr-2" /> {replyOpen ? "Hide reply" : "Add reply"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm justify-start"
              onClick={() => setShopOpen(true)}
              data-testid={`colony-shopping-${ticket.id}`}
            >
              <ShoppingCart className="w-3.5 h-3.5 mr-2" /> Add to shopping list
            </Button>
            {isMine && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm justify-start"
                onClick={() => setEditOpen(true)}
                disabled={busy}
                data-testid={`colony-edit-${ticket.id}`}
              >
                <Pencil className="w-3.5 h-3.5 mr-2" /> Edit ticket
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm justify-start border-purple-300 bg-background text-purple-700 hover:bg-purple-50"
              onClick={putOnHold}
              disabled={busy || ticket.status === "hold"}
              data-testid={`colony-hold-${ticket.id}`}
            >
              <PauseCircle className="w-3.5 h-3.5 mr-2" /> Put on hold
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm justify-start border-emerald-300 bg-background text-emerald-700 hover:bg-emerald-50"
              onClick={closeTicket}
              disabled={busy}
              data-testid={`colony-close-${ticket.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Close ticket
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-sm justify-start text-muted-foreground"
              onClick={() => setDateOpen((v) => !v)}
              data-testid={`colony-schedule-toggle-${ticket.id}`}
            >
              <CalendarClock className="w-3.5 h-3.5 mr-2" />
              {ticket.scheduled_for ? "Edit revisit date" : "Set revisit date"}
            </Button>
          </div>
        )}
      </div>

      {/* Optional revisit-date row — collapsed by default */}
      {!closed && dateOpen && (
        <div className="mt-3 border-t border-border pt-3 flex flex-wrap items-end gap-2" data-testid={`colony-schedule-form-${ticket.id}`}>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
              Revisit on (optional)
            </div>
            <Input
              type="date"
              value={pendingDate || ""}
              onChange={(e) => setPendingDate(e.target.value)}
              className="h-9 rounded-sm w-44"
              data-testid={`colony-schedule-input-${ticket.id}`}
            />
          </div>
          <Button size="sm" className="rounded-sm" onClick={saveDate} disabled={busy} data-testid={`colony-schedule-save-${ticket.id}`}>
            Save date
          </Button>
          {ticket.scheduled_for && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-sm text-muted-foreground"
              onClick={clearDate}
              disabled={busy}
              data-testid={`colony-schedule-clear-${ticket.id}`}
            >
              Clear
            </Button>
          )}
          <p className="text-xs text-muted-foreground w-full mt-1">
            This is a memo for the office — nothing changes automatically on that day.
          </p>
        </div>
      )}

      {/* Reply form */}
      {!closed && replyOpen && (
        <ColonyReplyForm
          ticketId={ticket.id}
          colonyId={ticket.colony_id}
          onDone={(updated) => { setReplyOpen(false); onUpdated(updated); }}
        />
      )}

      {/* Threaded notes — both admin and colony authors */}
      {ticket.notes?.length > 0 && (
        <div className="mt-4 border-t border-border pt-3 space-y-2" data-testid={`colony-ticket-notes-${ticket.id}`}>
          <CollapseToggle
            open={threadOpen}
            onClick={() => setThreadOpen((v) => !v)}
            label={`Replies (${ticket.notes.length})`}
            testid={`colony-ticket-thread-toggle-${ticket.id}`}
          />
          {threadOpen && ticket.notes.map((n) => {
            const mine = n.author === "colony";
            return (
              <div
                key={n.id}
                className={`pl-3 py-2 rounded-r-sm border-l-2 ${
                  mine ? "bg-slate-50 border-slate-400" : "bg-emerald-50 border-emerald-600"
                }`}
              >
                <div className="text-xs text-muted-foreground">
                  <strong className="text-foreground">
                    {mine ? "You" : (n.author_name || n.author_email || "The office")}
                  </strong>
                  {" · "}<HoverTime iso={n.created_at} testId={`colony-ticket-note-time-${n.id}`} />
                </div>
                {n.body && <p className="text-sm mt-1 whitespace-pre-wrap"><Highlight text={atr(n.body)} query={highlight} /></p>}
                {(n.photos?.length || n.photo_count) ? (
                  <div className="mt-2">
                    <LazyPhotoStrip
                      photos={n.photos}
                      count={n.photo_count}
                      fetchPhotos={() => colonyApi.get(`/colony/tickets/${ticket.id}/photos`).then((r) => (r.data.note_photos || {})[n.id] || [])}
                      alt="update photo"
                      size="w-14 h-14"
                      cacheKey={`note-${n.id}`}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <TicketEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        ticket={ticket}
        saving={busy}
        onSave={async (p) => { await patch(p, "Ticket updated."); setEditOpen(false); }}
      />
      <AddToShoppingDialog
        open={shopOpen}
        onOpenChange={setShopOpen}
        client={colonyApi}
        path={`/colony/tickets/${ticket.id}/shopping`}
        ticketTitle={ticket.title}
      />
    </div>
  );
}

function ColonyReplyForm({ ticketId, colonyId, onDone }) {
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [shopItems, setShopItems] = useState([]);
  const fileRef = useRef(null);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: colonyApi, scope: "mgmt_usa_nj", colonyId });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const submit = async () => {
    const text = body.trim();
    if (!text && photos.length === 0) return toast.error("Add a note, a photo, or both.");
    setSaving(true);
    try {
      const { data } = await colonyApi.post(`/colony/tickets/${ticketId}/notes`, { body: text, photos });
      const nAdded = await submitShoppingItems(colonyApi, `/colony/tickets/${ticketId}/shopping`, shopItems);
      toast.success("Reply sent. The office has been notified.");
      if (nAdded) toast.success(`${nAdded} ${nAdded === 1 ? "product" : "products"} added to shopping list.`);
      onDone(data);
    } catch (e) {
      if (queueIfOffline(e, { portal: "colony", method: "post", path: `/colony/tickets/${ticketId}/notes`, body: { body: text, photos }, label: "Send reply" })) {
        setBody("");
        setPhotos([]);
        return;
      }
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3 space-y-2" data-testid={`colony-reply-form-${ticketId}`}>
      <Textarea
        placeholder="Write a note back to the office…  (you can also paste a picture here)"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onPaste={pasteToFiles(addFiles)}
        rows={3}
        className="rounded-sm"
        data-testid={`colony-reply-body-${ticketId}`}
      />
      <div className="flex flex-wrap gap-2 items-center">
        {photos.map((p, i) => (
          <div key={i} className="relative">
            <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-14 h-14 rounded-sm border border-border" testId={`colony-reply-preview-${ticketId}-${i}`} />
            <button
              type="button"
              onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
              className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center"
              aria-label="Remove photo"
              data-testid={`colony-ticket-reply-photo-remove-${ticketId}-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < 6 && (
          <PhotoAddButtons onFiles={addFiles} testidPrefix={`colony-reply-${ticketId}`} />
        )}
      </div>
      <ReplyShoppingSection items={shopItems} setItems={setShopItems} />
      <div className="flex justify-end">
        <Button
          size="sm"
          className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
          onClick={submit}
          disabled={saving}
          data-testid={`colony-reply-submit-${ticketId}`}
        >
          <Reply className="w-3.5 h-3.5 mr-2" /> {saving ? "Sending…" : "Send reply"}
        </Button>
      </div>
    </div>
  );
}


function NewTicketDialog({ open, onOpenChange, colonies, onSaved }) {
  const [colonyId, setColonyId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]); // data URLs
  const [scheduledFor, setScheduledFor] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setColonyId(colonies[0]?.id || "");
      setTitle("");
      setDescription("");
      setPhotos([]);
      setScheduledFor("");
      setUrgent(false);
      setDateOpen(false);
    }
  }, [open, colonies]);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: colonyApi, scope: "mgmt_usa_nj", colonyId });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const remove = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!colonyId) return toast.error("Choose which colony this is for.");
    if (!title.trim()) return toast.error("Give the request a short title.");
    setSaving(true);
    const body = {
      colony_id: colonyId,
      title: title.trim(),
      description: description.trim(),
      photos,
      scheduled_for: scheduledFor || null,
      urgent,
    };
    try {
      await colonyApi.post("/colony/tickets", body);
      toast.success("Request sent — we'll be in touch.");
      onSaved();
    } catch (e) {
      if (queueIfOffline(e, { portal: "colony", method: "post", path: "/colony/tickets", body, label: `Create request "${body.title}"` })) {
        onOpenChange(false);
      } else {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="colony-new-ticket-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New service request</DialogTitle>
          <DialogDescription>Tell us what needs doing and attach a photo if you can — it helps us diagnose faster.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {colonies.length > 1 && (
            <div>
              <Label>Colony</Label>
              <Select value={colonyId} onValueChange={setColonyId}>
                <SelectTrigger className="rounded-sm h-10" data-testid="ticket-colony-select">
                  <SelectValue placeholder="Choose colony" />
                </SelectTrigger>
                <SelectContent>
                  {colonies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Short title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Broken heater at unit 12"
              className="rounded-sm h-10"
              data-testid="ticket-title-input"
              maxLength={120}
            />
          </div>
          <div>
            <Label>Details</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={pasteToFiles(addFiles)}
              rows={4}
              placeholder="When it started, what you've tried, where it is on the property…  (you can also paste a picture here)"
              className="rounded-sm"
              data-testid="ticket-description-input"
            />
          </div>
          <label className="flex items-center gap-2 border border-red-200 bg-red-50/60 rounded-sm px-3 py-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={urgent}
              onChange={(e) => setUrgent(e.target.checked)}
              className="w-4 h-4 accent-red-600"
              data-testid="colony-new-ticket-urgent"
            />
            <span className="text-sm font-semibold text-red-700">This is urgent</span>
            <span className="text-xs text-red-600/70 ml-auto">Shows red &amp; jumps to the top</span>
          </label>
          <div>
            <Label className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Photos (up to 6)
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/quicktime,video/mp4,video/webm"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
              data-testid="ticket-photo-input"
            />
            <div className="flex flex-wrap gap-2 mt-2" onPaste={pasteToFiles(addFiles)}>
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-16 h-16 rounded-sm border border-border" testId={`ticket-photo-preview-${i}`} />
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center"
                    data-testid={`ticket-photo-remove-${i}`}
                    aria-label="Remove photo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 6 && (
                <PhotoAddButtons onFiles={addFiles} testidPrefix="ticket-photo" />
              )}
            </div>
          </div>
          {!dateOpen ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-sm px-2 text-muted-foreground"
              onClick={() => setDateOpen(true)}
              data-testid="ticket-schedule-toggle"
            >
              <CalendarClock className="w-3.5 h-3.5 mr-2" /> Schedule for later (optional)
            </Button>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label>Date &amp; time</Label>
                <Input
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  className="rounded-sm h-10"
                  data-testid="ticket-schedule-date"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-sm text-muted-foreground"
                onClick={() => { setScheduledFor(""); setDateOpen(false); }}
                data-testid="ticket-schedule-clear"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Remove date
              </Button>
              <p className="text-xs text-muted-foreground w-full">
                Pick a future date &amp; time and the request stays under &quot;Scheduled&quot; until then — the office is notified when it goes live.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="ticket-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="ticket-submit"
          >
            {saving ? "Sending…" : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
